import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useRoleContext } from '../hooks/useRoleContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  User,
  Bell,
  Globe,
  Clock,
  Shield,
  CreditCard,
  Loader2,
  Check,
  ChevronRight,
  Settings as SettingsIcon,
  HelpCircle,
  Mail,
  Download,
  Trash2,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { userAPI } from '../lib/api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import {
  SETTINGS_HELP_LINKS,
  SETTINGS_HELP_SECTION_TEST_ID,
  SETTINGS_CLIENT_PREMIUM_TEST_ID,
  SETTINGS_LANGUAGE_SECTION_TEST_ID,
  SETTINGS_NAV_ITEMS,
  SETTINGS_PAGE_TEST_ID,
  SETTINGS_PRIVACY_SECTION_TEST_ID,
} from '../lib/settingsDisplay';
import ClientPremiumPanel from '../components/client/ClientPremiumPanel';
import ExperienceSettings from '../experience/settings/ExperienceSettings';
import { settingsLanguageOptions } from '../lib/supportedLocales';
import { arePaymentsEnabled } from '../lib/paymentsAvailability';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const paymentsEnabled = arePaymentsEnabled();

const TAB_ICONS = {
  profile: User,
  notifications: Bell,
  preferences: Globe,
  experience: Sparkles,
  privacy: Shield,
  help: HelpCircle,
  subscription: CreditCard,
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, loading: authLoading } = useAuth();
  const { isPureClientAccount } = useRoleContext();
  const { t, locale, setLocale } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [paymentStatus, setPaymentStatus] = useState(null);
  // GDPR data-subject controls (privacy tab)
  const [exporting, setExporting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Check for payment callback (only when payments enabled)
  useEffect(() => {
    if (!paymentsEnabled) {
      return;
    }
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    
    if (sessionId && payment === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment verification timed out. Please check your email.' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid' || data.payment_status === 'completed') {
          setPaymentStatus({ status: 'success', tier: data.tier });
          toast.success(`Subscription upgraded to ${data.tier}!`);
          fetchData(); // Refresh subscription data
          // Clear URL params
          navigate('/settings', { replace: true });
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus({ status: 'expired', message: 'Payment session expired.' });
          return;
        }
      }

      // Continue polling
      setPaymentStatus({ status: 'processing', message: 'Processing payment...' });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setPaymentStatus({ status: 'error', message: 'Error verifying payment.' });
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [settingsRes, subRes] = await Promise.all([
        fetch(`${API_URL}/api/user/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/user/subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData);
      }
      
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/user/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Settings saved successfully!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // GDPR Art. 20 — download a copy of the caller's personal data as JSON.
  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await userAPI.exportData();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tla-housing-europe-data-export.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('settings.privacy_export_success', 'Your data export has downloaded.'));
    } catch (error) {
      toast.error(t('settings.privacy_export_error', "We couldn't export your data. Please try again."));
    } finally {
      setExporting(false);
    }
  };

  // GDPR Art. 17 — password-gated account closure (anonymize + deactivate).
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError(t('settings.privacy_delete_password_required', 'Please enter your password to confirm.'));
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await userAPI.deleteAccount(deletePassword);
      toast.success(t('settings.privacy_delete_success', 'Your account has been closed and your data anonymized.'));
      // Clear local session and leave the authenticated app entirely.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setDeleteError(detail || t('settings.privacy_delete_error', 'Account closure failed. Check your password and try again.'));
      setDeleting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const tabs = SETTINGS_NAV_ITEMS.map((item) => ({
    id: item.id,
    label: t(item.labelKey, item.defaultLabel),
    icon: TAB_ICONS[item.id] || SettingsIcon,
    testId: item.testId,
  }));

  const handleLocaleChange = (value) => {
    updateSetting('locale', value);
    setLocale(value);
  };

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid={SETTINGS_PAGE_TEST_ID}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-signal-blue/20 rounded-lg flex items-center justify-center">
            <SettingsIcon className="w-6 h-6 text-signal-blue" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-800">{t('settings.title', 'SETTINGS')}</h1>
            <p className="text-slate-600">{t('settings.subtitle', 'Manage your account and preferences')}</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="md:w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-sm border border-slate-200 p-2 space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={tab.testId}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-signal-blue/20 text-signal-blue'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1">
            {/* Profile Tab */}
            {activeTab === 'profile' && settings && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
                <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {t('settings.profile_info', 'Profile Information')}
                </h2>

                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-700">{t('settings.full_name', 'Full Name')}</Label>
                    <Input
                      value={settings.name || ''}
                      onChange={(e) => updateSetting('name', e.target.value)}
                      className="bg-white border-slate-300 text-slate-800 mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700">{t('settings.email', 'Email')}</Label>
                    <Input
                      value={settings.email || ''}
                      disabled
                      className="bg-slate-800 border-slate-300 text-slate-600 mt-1"
                    />
                    <p className="text-xs text-slate-800 mt-1">{t('settings.email_readonly', 'Email cannot be changed')}</p>
                  </div>

                  <div>
                    <Label className="text-slate-700">{t('settings.phone', 'Phone Number')}</Label>
                    <Input
                      value={settings.phone || ''}
                      onChange={(e) => updateSetting('phone', e.target.value)}
                      placeholder="+49 123 456 7890"
                      className="bg-white border-slate-300 text-slate-800 mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700">{t('settings.role', 'Role')}</Label>
                    <div className="mt-1">
                      <Badge className={`${
                        settings.role === 'ADMIN' ? 'bg-red-500/20 text-red-400' :
                        settings.role === 'OWNER' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {settings.role}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-signal-blue hover:bg-signal-blue-hover text-white"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('settings.saving', 'Saving...')}</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> {t('settings.save_changes', 'Save Changes')}</>
                  )}
                </Button>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && settings && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
                <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  {t('settings.notifications_title', 'Notification Preferences')}
                </h2>

                <div className="space-y-6">
                  <div className="flex items-center justify-between py-3 border-b border-slate-200">
                    <div>
                      <p className="text-slate-800 font-medium">{t('settings.email_notif', 'Email Notifications')}</p>
                      <p className="text-sm text-slate-800">{t('settings.email_notif_desc', 'Receive updates via email')}</p>
                    </div>
                    <Switch
                      checked={settings.email_notifications}
                      onCheckedChange={(checked) => updateSetting('email_notifications', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-slate-200">
                    <div>
                      <p className="text-slate-800 font-medium">{t('settings.inapp_notif', 'In-App Notifications')}</p>
                      <p className="text-sm text-slate-800">{t('settings.inapp_notif_desc', 'Show notifications in the app')}</p>
                    </div>
                    <Switch
                      checked={settings.inapp_notifications}
                      onCheckedChange={(checked) => updateSetting('inapp_notifications', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-slate-200">
                    <div>
                      <p className="text-slate-800 font-medium">{t('settings.weekly_reports', 'Weekly Reports')}</p>
                      <p className="text-sm text-slate-800">{t('settings.weekly_reports_desc', 'Receive weekly performance summaries')}</p>
                    </div>
                    <Switch
                      checked={settings.weekly_reports}
                      onCheckedChange={(checked) => updateSetting('weekly_reports', checked)}
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700 mb-2 block">{t('settings.alert_frequency', 'Alert Frequency')}</Label>
                    <Select
                      value={settings.alert_frequency || 'instant'}
                      onValueChange={(value) => updateSetting('alert_frequency', value)}
                    >
                      <SelectTrigger className="bg-white border-slate-300 text-slate-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-300">
                        <SelectItem value="instant" className="text-slate-700">{t('settings.freq_instant', 'Instant')}</SelectItem>
                        <SelectItem value="daily" className="text-slate-700">{t('settings.freq_daily', 'Daily Digest')}</SelectItem>
                        <SelectItem value="weekly" className="text-slate-700">{t('settings.freq_weekly', 'Weekly Digest')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-signal-blue hover:bg-signal-blue-hover text-white"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('settings.saving', 'Saving...')}</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> {t('settings.save_changes', 'Save Changes')}</>
                  )}
                </Button>
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && isPureClientAccount && (
              <div data-testid={SETTINGS_CLIENT_PREMIUM_TEST_ID}>
                <ClientPremiumPanel
                  t={t}
                  showDashboardLink={false}
                  showPricingUnavailableNote
                  benefitTestIdPrefix="settings-premium-benefit"
                />
              </div>
            )}

            {activeTab === 'subscription' && !isPureClientAccount && subscription && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
                <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {t('settings.subscription_title', 'Subscription')}
                </h2>

                <div className="bg-slate-800 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-slate-400 text-sm">{t('settings.current_plan', 'Current Plan')}</p>
                      <p className="text-2xl font-bold text-white">
                        {subscription.package_name || subscription.tier}
                      </p>
                    </div>
                    <Badge className={`${
                      subscription.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-600'
                    }`}>
                      {subscription.status || 'Active'}
                    </Badge>
                  </div>

                  {subscription.next_billing_date && (
                    <p className="text-sm text-slate-400">
                      {t('settings.next_billing', 'Next billing:')} {new Date(subscription.next_billing_date).toLocaleDateString()}
                    </p>
                  )}

                  <Button
                    onClick={() => navigate('/owner/plans')}
                    className="mt-4 bg-military-gold hover:bg-military-gold/90 text-slate-950"
                  >
                    {subscription.tier === 'RECON' ? t('settings.upgrade_plan', 'Upgrade Plan') : t('settings.manage_plan', 'Manage Plan')}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {/* Features */}
                {subscription.features && (
                  <div>
                    <h3 className="font-medium text-slate-800 mb-3">{t('settings.your_features', 'Your Features')}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(subscription.features).slice(0, 6).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2 text-sm">
                          {value ? (
                            <Check className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <span className="w-4 h-4 text-slate-600">—</span>
                          )}
                          <span className={value ? 'text-slate-700' : 'text-slate-400'}>
                            {key.replace(/_/g, ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && settings && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
                <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  {t('settings.preferences_title', 'Preferences')}
                </h2>

                <div className="space-y-4" data-testid={SETTINGS_LANGUAGE_SECTION_TEST_ID}>
                  <div>
                    <Label className="text-slate-700 mb-2 block">{t('settings.language', 'Language')}</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      {t(
                        'settings.language_note',
                        'English and German have full platform translations. Other languages may be partial.',
                      )}
                    </p>
                    <Select
                      value={locale || settings.locale || 'en'}
                      onValueChange={handleLocaleChange}
                    >
                      <SelectTrigger className="bg-white border-slate-300 text-slate-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-300">
                        {settingsLanguageOptions().map((option) => (
                          <SelectItem key={option.code} value={option.code} className="text-slate-700">
                            {t(option.labelKey, option.defaultLabel)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-slate-700 mb-2 block">{t('settings.timezone', 'Timezone')}</Label>
                    <Select
                      value={settings.timezone || 'Europe/Berlin'}
                      onValueChange={(value) => updateSetting('timezone', value)}
                    >
                      <SelectTrigger className="bg-white border-slate-300 text-slate-800">
                        <Clock className="w-4 h-4 mr-2 text-slate-500" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-300">
                        <SelectItem value="Europe/Berlin" className="text-slate-700">Europe/Berlin (CET)</SelectItem>
                        <SelectItem value="Europe/London" className="text-slate-700">Europe/London (GMT)</SelectItem>
                        <SelectItem value="Europe/Rome" className="text-slate-700">Europe/Rome (CET)</SelectItem>
                        <SelectItem value="Europe/Madrid" className="text-slate-700">Europe/Madrid (CET)</SelectItem>
                        <SelectItem value="America/New_York" className="text-slate-700">America/New York (EST)</SelectItem>
                        <SelectItem value="America/Los_Angeles" className="text-slate-700">America/Los Angeles (PST)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-signal-blue hover:bg-signal-blue-hover text-white"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('settings.saving', 'Saving...')}</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> {t('settings.save_changes', 'Save Changes')}</>
                  )}
                </Button>
              </div>
            )}

            {activeTab === 'experience' && <ExperienceSettings />}

            {activeTab === 'privacy' && (
              <div
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4"
                data-testid={SETTINGS_PRIVACY_SECTION_TEST_ID}
              >
                <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {t('settings.privacy_title', 'Privacy & Data')}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t(
                    'settings.privacy_desc',
                    'Your account data is used to manage bookings, messages, and preferences. You can export a copy of your data or close your account below.',
                  )}
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button asChild variant="outline">
                    <Link to="/privacy">{t('settings.privacy_policy_link', 'Privacy policy')}</Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/terms">{t('settings.terms_link', 'Terms of use')}</Link>
                  </Button>
                </div>

                {/* Data export — GDPR Article 20 */}
                <div className="border-t border-slate-200 pt-5">
                  <h3 className="font-semibold text-slate-900">
                    {t('settings.privacy_export_title', 'Export your data')}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                    {t(
                      'settings.privacy_export_desc',
                      'Download a copy of the personal data we hold for your account, as a JSON file (GDPR Article 20).',
                    )}
                  </p>
                  <Button
                    onClick={handleExportData}
                    disabled={exporting}
                    variant="outline"
                    className="mt-3"
                    data-testid="settings-privacy-export-button"
                  >
                    {exporting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('settings.privacy_export_loading', 'Preparing…')}</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> {t('settings.privacy_export_button', 'Download my data')}</>
                    )}
                  </Button>
                </div>

                {/* Account closure — GDPR Article 17 */}
                <div className="border-t border-slate-200 pt-5">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    {t('settings.privacy_delete_title', 'Close your account')}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                    {t(
                      'settings.privacy_delete_desc',
                      'Closing your account anonymizes your personal data and deactivates access. Records we must keep for legal or accounting reasons are retained in anonymized form (GDPR Article 17). This cannot be undone.',
                    )}
                  </p>
                  <Button
                    onClick={() => { setDeleteError(''); setDeletePassword(''); setDeleteDialogOpen(true); }}
                    className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="settings-privacy-delete-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('settings.privacy_delete_button', 'Close my account')}
                  </Button>
                </div>

                <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { if (!deleting) setDeleteDialogOpen(open); }}>
                  <AlertDialogContent data-testid="settings-privacy-delete-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('settings.privacy_delete_dialog_title', 'Close your account?')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t(
                          'settings.privacy_delete_dialog_desc',
                          'This permanently anonymizes your data and signs you out. Enter your password to confirm.',
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2">
                      <Label className="text-slate-700">
                        {t('settings.privacy_delete_password_label', 'Password')}
                      </Label>
                      <Input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder={t('settings.privacy_delete_password_placeholder', 'Enter your password')}
                        className="bg-white border-slate-300 text-slate-800"
                        data-testid="settings-privacy-delete-password"
                        autoComplete="current-password"
                      />
                      {deleteError && (
                        <p className="text-sm text-red-600" data-testid="settings-privacy-delete-error">{deleteError}</p>
                      )}
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={deleting}>
                        {t('settings.privacy_delete_cancel', 'Cancel')}
                      </AlertDialogCancel>
                      <Button
                        onClick={handleDeleteAccount}
                        disabled={deleting}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="settings-privacy-delete-confirm"
                      >
                        {deleting ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('settings.privacy_delete_loading', 'Closing…')}</>
                        ) : (
                          t('settings.privacy_delete_confirm', 'Permanently close account')
                        )}
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}

            {activeTab === 'help' && (
              <div className="space-y-4" data-testid={SETTINGS_HELP_SECTION_TEST_ID}>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
                  <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    {t('settings.help_title', 'Help & Support')}
                  </h2>
                  <p className="text-sm text-slate-600">
                    {t('settings.help_desc', 'Find answers, contact support, or review getting-started guidance.')}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {SETTINGS_HELP_LINKS.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-800 hover:bg-slate-50"
                        data-testid={link.testId}
                      >
                        <span>{t(link.labelKey, link.defaultLabel)}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-signal-blue shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {t('settings.contact_support_title', 'Contact support')}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        {t('settings.contact_support_desc', 'Need help with a booking or account issue? Reach out to our team.')}
                      </p>
                      <Button asChild variant="outline" className="mt-3">
                        <Link to="/contact">{t('settings.help_contact_link', 'Contact support')}</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
